/**
 * Admin Users — searchable table + actions (toggle Pro, grant Pro days, reset password,
 * force-logout, suspend, delete, create new user).
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Search, Plus, Crown, UserX, KeyRound, LogOut, Trash2,
  ShieldOff, ShieldCheck, ShieldPlus, ShieldMinus, X, Download,
} from "lucide-react";

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showGrant, setShowGrant] = useState(null); // user object
  const [showResetPw, setShowResetPw] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/admin/users?q=${encodeURIComponent(q)}&limit=50`);
      setUsers(data.users);
      setTotal(data.total);
      setSelected(new Set());
    } catch (e) {
      toast.error("Could not load users");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  // Eligible (non-admin) users on the current page — used for "select all" semantics
  const eligibleUsers = useMemo(() => users.filter((u) => u.role !== "admin"), [users]);
  const allEligibleSelected = eligibleUsers.length > 0 && eligibleUsers.every((u) => selected.has(u.id));
  const someEligibleSelected = eligibleUsers.some((u) => selected.has(u.id));

  const toggleOne = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const toggleAll = () => {
    if (allEligibleSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(eligibleUsers.map((u) => u.id)));
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`DELETE ${ids.length} user(s)? This wipes ALL their data and cannot be undone. Admin accounts in the selection will be skipped.`)) return;
    setBulkDeleting(true);
    try {
      const res = await api.post("/admin/users/bulk-delete", { user_ids: ids });
      const skippedCount = (res.skipped || []).length;
      if (skippedCount > 0) {
        toast.success(`Deleted ${res.deleted}. Skipped ${skippedCount} admin/self account(s).`);
      } else {
        toast.success(`Deleted ${res.deleted} user(s).`);
      }
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSuspend = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/${u.suspended ? "unsuspend" : "suspend"}`);
      toast.success(u.suspended ? "Reactivated" : "Suspended");
      load();
    } catch { toast.error("Action failed"); }
  };

  const toggleAdmin = async (u) => {
    const makeAdmin = u.role !== "admin";
    const msg = makeAdmin
      ? `Give ${u.email} full admin access? They will be able to manage all users and content.`
      : `Remove admin access from ${u.email}? They will become a normal user.`;
    if (!window.confirm(msg)) return;
    try {
      const res = await api.post(`/admin/users/${u.id}/${makeAdmin ? "make-admin" : "remove-admin"}`);
      toast.success(res?.message || "Done");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const logoutAll = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/logout-all`);
      toast.success("All sessions invalidated");
    } catch { toast.error("Action failed"); }
  };

  const revokePro = async (u) => {
    if (!window.confirm(`Revoke Pro for ${u.email}?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/revoke-pro`);
      toast.success("Pro revoked");
      load();
    } catch { toast.error("Action failed"); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`DELETE ${u.email}? This wipes ALL their data and cannot be undone.`)) return;
    try {
      const axiosLib = (await import("axios")).default;
      await axiosLib.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${u.id}`, { withCredentials: true });
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Users</h2>
          <p className="text-sm text-muted-foreground">{total} total — manage accounts and memberships</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            data-testid="admin-users-export-csv"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/export.csv`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-card text-sm font-medium hover:bg-muted"
            title="Download all members as CSV — ready for Mailchimp / ConvertKit / Resend import"
          >
            <Download className="w-4 h-4" /> Export CSV
          </a>
          <button
            data-testid="admin-create-user-btn"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
          >
            <Plus className="w-4 h-4" /> New user
          </button>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          data-testid="admin-users-search"
          type="text"
          placeholder="Search by email or name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-card border border-border text-sm focus:outline-none focus:border-primary/40"
        />
      </div>

      {/* Bulk-action toolbar */}
      {selected.size > 0 && (
        <div
          data-testid="admin-users-bulk-bar"
          className="mb-3 flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-primary/10 border border-primary/30 text-sm"
        >
          <span className="font-medium" data-testid="admin-users-bulk-count">
            {selected.size} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-users-bulk-clear"
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 rounded-lg text-muted-foreground hover:bg-secondary text-xs"
            >
              Clear
            </button>
            <button
              data-testid="admin-users-bulk-delete"
              onClick={bulkDelete}
              disabled={bulkDeleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-destructive text-destructive-foreground text-xs font-medium disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {bulkDeleting ? "Deleting…" : `Delete ${selected.size}`}
            </button>
          </div>
        </div>
      )}

      <div className="border border-border rounded-2xl overflow-hidden bg-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium w-10">
                <input
                  type="checkbox"
                  data-testid="admin-users-select-all"
                  aria-label="Select all eligible users"
                  checked={allEligibleSelected}
                  ref={(el) => { if (el) el.indeterminate = !allEligibleSelected && someEligibleSelected; }}
                  onChange={toggleAll}
                  className="w-4 h-4 cursor-pointer accent-primary"
                />
              </th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Pro</th>
              <th className="px-4 py-3 font-medium">Pro until</th>
              <th className="px-4 py-3 font-medium">Joined</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">No users</td></tr>
            )}
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid={`admin-user-row-${u.id}`}>
                <td className="px-4 py-3">
                  {u.role !== "admin" ? (
                    <input
                      type="checkbox"
                      data-testid={`admin-user-select-${u.id}`}
                      aria-label={`Select ${u.email}`}
                      checked={selected.has(u.id)}
                      onChange={() => toggleOne(u.id)}
                      className="w-4 h-4 cursor-pointer accent-primary"
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground" title="Admins can't be bulk-deleted">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium">{u.email}</div>
                  {u.name && <div className="text-xs text-muted-foreground">{u.name}</div>}
                  {u.suspended && <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">Suspended</span>}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>{u.role}</span>
                </td>
                <td className="px-4 py-3">
                  {u.is_pro_now
                    ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-warning/15 text-warning"><Crown className="w-3 h-3" /> Pro</span>
                    : <span className="text-xs text-muted-foreground">Free</span>}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{fmt(u.pro_until)}</td>
                <td className="px-4 py-3 text-muted-foreground">{fmt(u.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button title="Grant Pro days" data-testid={`grant-pro-${u.id}`} onClick={() => setShowGrant(u)} className="p-1.5 rounded hover:bg-secondary"><Crown className="w-4 h-4 text-warning" /></button>
                    {u.is_pro_now && (
                      <button title="Revoke Pro" data-testid={`revoke-pro-${u.id}`} onClick={() => revokePro(u)} className="p-1.5 rounded hover:bg-secondary"><UserX className="w-4 h-4 text-muted-foreground" /></button>
                    )}
                    <button title="Reset password" data-testid={`reset-pw-${u.id}`} onClick={() => setShowResetPw(u)} className="p-1.5 rounded hover:bg-secondary"><KeyRound className="w-4 h-4 text-muted-foreground" /></button>
                    <button title="Force logout all devices" data-testid={`logout-all-${u.id}`} onClick={() => logoutAll(u)} className="p-1.5 rounded hover:bg-secondary"><LogOut className="w-4 h-4 text-muted-foreground" /></button>
                    <button title={u.suspended ? "Reactivate" : "Suspend"} data-testid={`suspend-${u.id}`} onClick={() => toggleSuspend(u)} className="p-1.5 rounded hover:bg-secondary">
                      {u.suspended ? <ShieldCheck className="w-4 h-4 text-success" /> : <ShieldOff className="w-4 h-4 text-muted-foreground" />}
                    </button>
                    <button
                      title={u.role === "admin" ? "Remove admin access" : "Make admin"}
                      data-testid={`toggle-admin-${u.id}`}
                      onClick={() => toggleAdmin(u)}
                      className="p-1.5 rounded hover:bg-secondary"
                    >
                      {u.role === "admin"
                        ? <ShieldMinus className="w-4 h-4 text-destructive" />
                        : <ShieldPlus className="w-4 h-4 text-primary" />}
                    </button>
                    {u.role !== "admin" && (
                      <button title="Delete user" data-testid={`delete-${u.id}`} onClick={() => deleteUser(u)} className="p-1.5 rounded hover:bg-destructive/15"><Trash2 className="w-4 h-4 text-destructive" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={load} />}
      {showGrant && <GrantProModal user={showGrant} onClose={() => setShowGrant(null)} onDone={load} />}
      {showResetPw && <ResetPasswordModal user={showResetPw} onClose={() => setShowResetPw(null)} />}
    </div>
  );
};

const ModalShell = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid={testid}>
    <div className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-md overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <h3 className="font-semibold">{title}</h3>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-secondary" data-testid="modal-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const CreateUserModal = ({ onClose, onCreated }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [grantDays, setGrantDays] = useState(0);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", { email, password, name: name || null, grant_pro_days: Number(grantDays) || 0 });
      toast.success("User created");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create user");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title="Create new user" onClose={onClose} testid="create-user-modal">
      <form onSubmit={submit} className="space-y-3">
        <input data-testid="new-user-email" required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        <input data-testid="new-user-password" required type="text" placeholder="Password (min 8 chars)" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        <input data-testid="new-user-name" type="text" placeholder="Display name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        <div>
          <label className="text-xs text-muted-foreground">Grant Pro for N days (0 = free user)</label>
          <input data-testid="new-user-grant-days" type="number" min={0} value={grantDays} onChange={(e) => setGrantDays(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 px-3 py-2 rounded-lg border border-border text-sm">Cancel</button>
          <button type="submit" disabled={saving} data-testid="new-user-submit" className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-50">
            {saving ? "Creating…" : "Create user"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

const GrantProModal = ({ user, onClose, onDone }) => {
  const [days, setDays] = useState(30);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post(`/admin/users/${user.id}/grant-pro`, { days: Number(days) });
      toast.success(`Pro granted until ${new Date(res.pro_until).toLocaleDateString()}`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not grant Pro");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title={`Grant Pro to ${user.email}`} onClose={onClose} testid="grant-pro-modal">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-muted-foreground">Add Pro days to this account. If user already has Pro, this extends from the current expiry.</p>
        <input data-testid="grant-pro-days" required type="number" min={1} max={3650} value={days} onChange={(e) => setDays(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 px-3 py-2 rounded-lg border border-border text-sm">Cancel</button>
          <button type="submit" disabled={saving} data-testid="grant-pro-submit" className="flex-1 px-3 py-2 rounded-lg bg-warning text-warning-foreground text-sm disabled:opacity-50">
            {saving ? "Granting…" : `Grant ${days} days`}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

const ResetPasswordModal = ({ user, onClose }) => {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/users/${user.id}/reset-password`, { new_password: pw });
      toast.success("Password reset & all sessions invalidated");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title={`Reset password — ${user.email}`} onClose={onClose} testid="reset-password-modal">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-muted-foreground">Set a new password for this user. All their existing sessions will be force-logged-out.</p>
        <input data-testid="reset-pw-input" required type="text" minLength={8} placeholder="New password (min 8 chars)" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 px-3 py-2 rounded-lg border border-border text-sm">Cancel</button>
          <button type="submit" disabled={saving} data-testid="reset-pw-submit" className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-50">
            {saving ? "Resetting…" : "Reset password"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

export default AdminUsersPage;
